// lib/services/resource/glb_loader.ts

import {
  ModelData,
  MeshPrimitiveData,
  NodeData,
  SkinData,
  AnimationClipData,
  AnimationChannelData,
} from "../geometry/modeldata";

type Gltf = {
  buffers?: any[];
  bufferViews?: any[];
  accessors?: any[];
  meshes?: any[];
  nodes?: any[];
  skins?: any[];
  animations?: any[];
};

const GLB_MAGIC = 0x46546c67;
const JSON_CHUNK_TYPE = 0x4e4f534a;
const BIN_CHUNK_TYPE = 0x004e4942;

export default class GlbLoader {
  async load(arrayBuffer: ArrayBuffer): Promise<ModelData> {
    const { json, bin } = this.parseGlb(arrayBuffer);

    const nodes = this.readNodes(json);
    const meshes = this.readMeshes(json, bin, nodes);
    const skins = this.readSkins(json, bin);
    const animations = this.readAnimations(json, bin,nodes,skins,meshes);

    return {
    meshes,
    renderMeshes: [],
    nodes,
    skins,
    animations,
};
  }

  private parseGlb(arrayBuffer: ArrayBuffer): { json: Gltf; bin: Uint8Array } {
    const dv = new DataView(arrayBuffer);

    const magic = dv.getUint32(0, true);
    const version = dv.getUint32(4, true);
    const length = dv.getUint32(8, true);

    if (magic !== GLB_MAGIC) throw new Error("Invalid GLB magic");
    if (version !== 2) throw new Error(`Unsupported GLB version: ${version}`);
    if (length !== arrayBuffer.byteLength) throw new Error("GLB length mismatch");

    let offset = 12;
    let json: Gltf | null = null;
    let bin = new Uint8Array(0);

    while (offset < length) {
      const chunkLength = dv.getUint32(offset, true);
      const chunkType = dv.getUint32(offset + 4, true);
      const chunkStart = offset + 8;
      const chunkEnd = chunkStart + chunkLength;

      if (chunkType === JSON_CHUNK_TYPE) {
        const jsonBytes = new Uint8Array(arrayBuffer, chunkStart, chunkLength);
        const jsonText = new TextDecoder().decode(jsonBytes);
        json = JSON.parse(jsonText);
      } else if (chunkType === BIN_CHUNK_TYPE) {
        bin = new Uint8Array(arrayBuffer, chunkStart, chunkLength);
      }

      offset = chunkEnd;
    }

    if (!json) throw new Error("GLB missing JSON chunk");

    return { json, bin };
  }

  private readNodes(json: Gltf): NodeData[] {
    const srcNodes = json.nodes ?? [];
    const nodes: NodeData[] = srcNodes.map((n: any) => ({
      name: n.name,
      parentIndex: -1,
      children: n.children ?? [],
      translation: new Float32Array(n.translation ?? [0, 0, 0]),
      rotation: new Float32Array(n.rotation ?? [0, 0, 0, 1]),
      scale: new Float32Array(n.scale ?? [1, 1, 1]),
      meshIndex: n.mesh,
      skinIndex: n.skin,
     
    }));

    nodes.forEach((node, parentIndex) => 
    {
      for (const child of node.children) 
      {
        nodes[child].parentIndex = parentIndex;
      }
    });

    return nodes;
  }

  private readMeshes(json: Gltf, bin: Uint8Array, nodes: NodeData[]): MeshPrimitiveData[] {
    const meshes: MeshPrimitiveData[] = [];
    const srcMeshes = json.meshes ?? [];

    nodes.forEach((node, nodeIndex) => {
      if (node.meshIndex === undefined) return;

      const gltfMesh = srcMeshes[node.meshIndex];
      if (!gltfMesh?.primitives) return;

      for (const primitive of gltfMesh.primitives) {
        const attrs = primitive.attributes ?? {};

        const positions = this.readAccessorAsFloat32(json, bin, attrs.POSITION);
        const normals = attrs.NORMAL !== undefined
          ? this.readAccessorAsFloat32(json, bin, attrs.NORMAL)
          : undefined;
        const uvs = attrs.TEXCOORD_0 !== undefined
          ? this.readAccessorAsFloat32(json, bin, attrs.TEXCOORD_0)
          : undefined;

        const joints0 = attrs.JOINTS_0 !== undefined
          ? this.readAccessorAsIntArray(json, bin, attrs.JOINTS_0)
          : undefined;
        const weights0 = attrs.WEIGHTS_0 !== undefined
          ? this.readAccessorAsFloat32(json, bin, attrs.WEIGHTS_0)
          : undefined;

        const indices = primitive.indices !== undefined
          ? this.readAccessorAsIndexArray(json, bin, primitive.indices)
          : undefined;

        meshes.push({
          name: gltfMesh.name,
          nodeIndex,
          positions,
          normals,
          uvs,
          indices,
          joints0,
          weights0,
          skinned: joints0 !== undefined && weights0 !== undefined,
          skinIndex: node.skinIndex,
        });
      }
    });

    return meshes;
  }

  private readSkins(json: Gltf, bin: Uint8Array): SkinData[] {
  const srcSkins = json.skins ?? [];

  return srcSkins.map((skin: any) => {
    const joints: number[] = skin.joints ?? [];
    const inverseBindMatrices: Float32Array[] = [];

    if (skin.inverseBindMatrices !== undefined) {
      const flat = this.readAccessorAsFloat32(json, bin, skin.inverseBindMatrices);
      for (let i = 0; i < flat.length; i += 16) {
        inverseBindMatrices.push(flat.slice(i, i + 16));
      }
    } else {
      for (let i = 0; i < joints.length; i++) {
        inverseBindMatrices.push(new Float32Array([
          1, 0, 0, 0,
          0, 1, 0, 0,
          0, 0, 1, 0,
          0, 0, 0, 1,
        ]));
      }
    }

    return {
      name: skin.name,
      joints,
      inverseBindMatrices,
      skeletonRoot: skin.skeleton,
    };
  });
}

private findHierarchyRootNodeIndex(nodes: NodeData[], nodeIndex: number): number {
  if (nodeIndex < 0) return -1;

  let current = nodeIndex;
  while (true) {
    const parent = nodes[current]?.parentIndex ?? -1;
    if (parent < 0) return current;
    current = parent;
  }
}

private detectRootMotionNodeIndex(
  clip: AnimationClipData,
  nodes: NodeData[],
  skins: SkinData[],
  skinIndex: number
): number {
  const skin = skins[skinIndex];
  const jointSet = new Set<number>(skin?.joints ?? []);

  let bestNodeIndex = -1;
  let bestScore = -Infinity;

  for (const channel of clip.channels) {
    if (channel.path !== "translation") continue;
    if (channel.times.length < 2 || channel.values.length < 6) continue;

    const startX = channel.values[0];
    const startY = channel.values[1];
    const startZ = channel.values[2];

    const endBase = channel.values.length - 3;
    const endX = channel.values[endBase + 0];
    const endY = channel.values[endBase + 1];
    const endZ = channel.values[endBase + 2];

    const dx = endX - startX;
    const dy = endY - startY;
    const dz = endZ - startZ;

    const horizontal = Math.hypot(dx, dz);
    const verticalPenalty = Math.abs(dy) * 0.25;

    let score = horizontal - verticalPenalty;

    const nodeName = nodes[channel.nodeIndex]?.name ?? "";
    if (jointSet.has(channel.nodeIndex)) score += 1000;
    if (/root|hips|pelvis/i.test(nodeName)) score += 10;

    if (score > bestScore) {
      bestScore = score;
      bestNodeIndex = channel.nodeIndex;
    }
  }

  return bestNodeIndex;
}

private findPrimarySkinIndexForClip(
  clip: AnimationClipData,
  meshes: MeshPrimitiveData[]
): number {
  const scoreBySkin = new Map<number, number>();

  for (const mesh of meshes) {
    if (!mesh.skinned) continue;
    if (mesh.skinIndex === undefined) continue;

    scoreBySkin.set(mesh.skinIndex, (scoreBySkin.get(mesh.skinIndex) ?? 0) + 1);
  }

  let bestSkinIndex = -1;
  let bestScore = -1;

  for (const [skinIndex, score] of scoreBySkin) {
    if (score > bestScore) {
      bestScore = score;
      bestSkinIndex = skinIndex;
    }
  }

  return bestSkinIndex;
}


private readAnimations(
  json: Gltf,
  bin: Uint8Array,
  nodes: NodeData[],
  skins: SkinData[],
  meshes: MeshPrimitiveData[]
): AnimationClipData[] {
  const srcAnimations = json.animations ?? [];

  return srcAnimations.map((anim: any, index: number) => {
    const channels: AnimationChannelData[] = [];
    let duration = 0;

    for (const channel of anim.channels ?? []) {
      const sampler = anim.samplers[channel.sampler];
      const target = channel.target;

      const times = this.readAccessorAsFloat32(json, bin, sampler.input);
      const values = this.readAccessorAsFloat32(json, bin, sampler.output);

      if (times.length > 0) {
        duration = Math.max(duration, times[times.length - 1]);
      }

      channels.push({
        nodeIndex: target.node,
        path: target.path,
        interpolation: sampler.interpolation ?? "LINEAR",
        times,
        values,
      });
    }

    const clip: AnimationClipData = {
      name: anim.name ?? `anim_${index}`,
      duration,
      channels,
      rootMotionNodeIndex: -1,
      hierarchyRootNodeIndex: -1,
    };

    const skinIndex = this.findPrimarySkinIndexForClip(clip, meshes);
     const isRootMotionClip = /_RM\b/i.test(clip.name);  

    if (skinIndex >= 0 && isRootMotionClip) 
    {
      clip.rootMotionNodeIndex = this.detectRootMotionNodeIndex(
        clip,
        nodes,
        skins,
        skinIndex
      );

      clip.hierarchyRootNodeIndex = this.findHierarchyRootNodeIndex(
        nodes,
        clip.rootMotionNodeIndex
      );
    }
    else
    {
       clip.rootMotionNodeIndex = -1;
       clip.hierarchyRootNodeIndex = -1;
    }

    /*console.log(
      `clip=${clip.name} rootMotion=${clip.rootMotionNodeIndex} (${nodes[clip.rootMotionNodeIndex]?.name}) hierarchyRoot=${clip.hierarchyRootNodeIndex} (${nodes[clip.hierarchyRootNodeIndex]?.name})`
    );*/

    return clip;
  });
}
 

  private readAccessorAsFloat32(json: Gltf, bin: Uint8Array, accessorIndex: number): Float32Array {
  const accessor = json.accessors?.[accessorIndex];
  if (!accessor) throw new Error(`Missing accessor ${accessorIndex}`);

  const raw = this.readAccessorBytes(json, bin, accessorIndex);
  const out = new Float32Array(accessor.count * this.getNumComponents(accessor.type));
  const normalized = accessor.normalized === true;

  switch (accessor.componentType) {
    case 5126: {
      return new Float32Array(raw.buffer, raw.byteOffset, out.length).slice();
    }
    case 5123: {
      const src = new Uint16Array(raw.buffer, raw.byteOffset, out.length);
      for (let i = 0; i < src.length; i++) {
        out[i] = normalized ? src[i] / 65535.0 : src[i];
      }
      return out;
    }
    case 5121: {
      const src = new Uint8Array(raw.buffer, raw.byteOffset, out.length);
      for (let i = 0; i < src.length; i++) {
        out[i] = normalized ? src[i] / 255.0 : src[i];
      }
      return out;
    }
    default:
      throw new Error(`Unsupported float accessor componentType ${accessor.componentType}`);
  }
}

  private readAccessorAsIntArray(
    json: Gltf,
    bin: Uint8Array,
    accessorIndex: number
  ): Uint8Array | Uint16Array {
    const accessor = json.accessors?.[accessorIndex];
    if (!accessor) throw new Error(`Missing accessor ${accessorIndex}`);

    const raw = this.readAccessorBytes(json, bin, accessorIndex);
    const count = accessor.count * this.getNumComponents(accessor.type);

    switch (accessor.componentType) {
      case 5121:
        return new Uint8Array(raw.buffer, raw.byteOffset, count).slice();
      case 5123:
        return new Uint16Array(raw.buffer, raw.byteOffset, count).slice();
      default:
        throw new Error(`Unsupported integer accessor componentType ${accessor.componentType}`);
    }
  }

  private readAccessorAsIndexArray(
    json: Gltf,
    bin: Uint8Array,
    accessorIndex: number
  ): Uint16Array | Uint32Array {
    const accessor = json.accessors?.[accessorIndex];
    if (!accessor) throw new Error(`Missing accessor ${accessorIndex}`);

    const raw = this.readAccessorBytes(json, bin, accessorIndex);
    const count = accessor.count;

    switch (accessor.componentType) {
      case 5123:
        return new Uint16Array(raw.buffer, raw.byteOffset, count).slice();
      case 5125:
        return new Uint32Array(raw.buffer, raw.byteOffset, count).slice();
      default:
        throw new Error(`Unsupported index componentType ${accessor.componentType}`);
    }
  }

  private readAccessorBytes(json: Gltf, bin: Uint8Array, accessorIndex: number): Uint8Array {
    const accessor = json.accessors?.[accessorIndex];
    const bufferView = json.bufferViews?.[accessor.bufferView];
    if (!accessor || !bufferView) throw new Error(`Invalid accessor ${accessorIndex}`);

    const accessorByteOffset = accessor.byteOffset ?? 0;
    const bufferViewByteOffset = bufferView.byteOffset ?? 0;

    const numComponents = this.getNumComponents(accessor.type);
    const componentSize = this.getComponentSize(accessor.componentType);
    const elementSize = numComponents * componentSize;
    const count = accessor.count;

    const byteStride = bufferView.byteStride ?? elementSize;
    const start = bufferViewByteOffset + accessorByteOffset;

    if (byteStride === elementSize) {
      const size = count * elementSize;
      return bin.slice(start, start + size);
    }

    const packed = new Uint8Array(count * elementSize);
    for (let i = 0; i < count; i++) {
      const srcOffset = start + i * byteStride;
      const dstOffset = i * elementSize;
      packed.set(bin.slice(srcOffset, srcOffset + elementSize), dstOffset);
    }
    return packed;
  }

  private getNumComponents(type: string): number {
    switch (type) {
      case "SCALAR": return 1;
      case "VEC2": return 2;
      case "VEC3": return 3;
      case "VEC4": return 4;
      case "MAT4": return 16;
      default:
        throw new Error(`Unsupported accessor type ${type}`);
    }
  }

  private getComponentSize(componentType: number): number {
    switch (componentType) {
      case 5121: return 1; // UNSIGNED_BYTE
      case 5123: return 2; // UNSIGNED_SHORT
      case 5125: return 4; // UNSIGNED_INT
      case 5126: return 4; // FLOAT
      default:
        throw new Error(`Unsupported componentType ${componentType}`);
    }
  }

}